import classNames from 'classnames';
import Icon from 'components/icon/Icon';
import Select, { components } from 'react-select';
import { ErrorMessage, getIn } from 'formik';
import InputErrorMessage from 'components/input/InputErrorMessage';
import { FormattedMessage, useIntl } from 'react-intl';
import messages from 'messages';
import Badge from 'components/badge/Badge';
import { useRef, useState } from 'react';

const selectStyles = {
  container: provided => ({
    ...provided
    // ...styles?.container, // overrule the default styles
  }),
  option: (provided, state) => ({
    ...provided,
    isDisabled: state.isSelected
    // ...styles?.option, // overrule the default styles
  }),
  control: provided => ({
    ...provided
  }),
  menu: provided => ({
    ...provided
  }),
  indicatorSeparator: () => null,
  singleValue: (provided, state) => {
    const opacity = state.isDisabled ? 0.5 : 1;
    const transition = 'opacity 300ms';

    return { ...provided, opacity, transition };
  }
};

const DropdownIndicator = props => {
  return (
    <components.DropdownIndicator {...props}>
      <Icon
        id="dropdown-open"
        extraClassNames={classNames('c-icon--input c-icon--input-left')}
      />
    </components.DropdownIndicator>
  );
};
const ClearIndicator = props => {
  return (
    <components.ClearIndicator {...props}>
      <Icon
        id="clear"
        extraClassNames={classNames('c-icon--input c-icon--input-left')}
      />
    </components.ClearIndicator>
  );
};

const ValueContainer = ({ children, ...props }) => {
  const intl = useIntl();

  if (children?.[0]?.length > 1) {
    return (
      <components.ValueContainer {...props}>
        <span
          title={intl.formatMessage(
            messages.selectCustomMultiValueFilteringAthletes,
            {
              value: props.selectProps.value.length,
              total: props.selectProps.options.length
            }
          )}
        >
          <FormattedMessage
            {...messages.selectCustomMultiValueFilteringAthletes}
            values={{
              value: props.selectProps.value.length,
              total: props.selectProps.options.length
            }}
          />
        </span>
        {children[children.length - 1]}
      </components.ValueContainer>
    );
  }

  return (
    <components.ValueContainer {...props}>{children}</components.ValueContainer>
  );
};

const MultiValueContainer = props => {
  const intl = useIntl();

  return (
    <span
      title={intl.formatMessage(
        messages.selectCustomMultiValueFilteringAthletes,
        {
          value: props.selectProps.value.length,
          total: props.selectProps.options.length
        }
      )}
    >
      <FormattedMessage
        {...messages.selectCustomMultiValueFilteringAthletes}
        values={{
          value: props.selectProps.value.length,
          total: props.selectProps.options.length
        }}
      />
    </span>
  );
};

const CustomSingleValue = props => {
  return (
    <components.SingleValue {...props}>
      <div
        title={props.data.label}
        style={{
          position: 'relative',
          zIndex: 1,
          textOverflow: 'ellipsis',
          overflow: 'hidden'
        }}
      >
        {props.children}
      </div>
    </components.SingleValue>
  );
};

const formatGroupLabel = data => (
  <>
    <span>{data.label}</span>
    <Badge variant="badge-neutral" lighterFont>
      {data.options.length}
    </Badge>
  </>
);

const Group = props => {
  return (
    <div className="c-input-select__group">
      <div className="c-input-select__group-heading">
        {props.data.label}
        <Badge variant="badge-neutral" lighterFont>
          {props.options.length}
        </Badge>
      </div>
      {props.children}
    </div>
  );
};

const FieldSelect = props => {
  const {
    id,
    readOnly,
    disabled,
    outfaded,
    inline,
    standalone,
    extraClassNames,
    label,
    labelGroup,
    labelHiddenVisually,
    name,
    options,
    value,
    isSearchable = false,
    noOptionsMessage,
    isClearable,
    isMulti,
    onChange,
    onBlur,
    customMultiValue,
    placeholder,
    defaultMenuIsOpen,
    errors,
    touched,
    maxMenuHeight = 150,
    minMenuHeight = 120,
    onTable
  } = props;
  const ref = useRef(null);
  const [menuOpen, setMenuOpen] = useState(defaultMenuIsOpen);
  const intl = useIntl();

  const handleMenuOpen = () => {
    setMenuOpen(false);
    if (ref.current && onTable) {
      const thElement = ref?.current?.inputRef.closest('th');

      const container = document.querySelector(
        '.c-panel__body--horizontal:has(> .c-table)'
      );
      const stickyTh = container.querySelector(
        '.c-table--benchmark thead th:first-child'
      ); // first sticky column
      const stickyRect = stickyTh.getBoundingClientRect();
      const thRect = thElement.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      const stickyRight = stickyRect.right;
      const thLeft = thRect.left;

      if (thLeft < stickyRight - 2 && thElement !== stickyTh) {
        const currentScrollLeft = container.scrollLeft;
        const offsetLeftRelativeToContainer = thRect.left - containerRect.left;
        const desiredScroll =
          offsetLeftRelativeToContainer +
          currentScrollLeft -
          stickyRect.width -
          32;

        let scrollTimeout;

        const onScrollEnd = () => {
          clearTimeout(scrollTimeout);
          scrollTimeout = setTimeout(() => {
            container.removeEventListener('scroll', onScrollEnd);
            if (ref?.current) {
              setMenuOpen(true);
            }
          }, 100);
        };

        container.addEventListener('scroll', onScrollEnd);
        container.scrollTo({
          left: desiredScroll,
          behavior: 'smooth'
        });
      } else {
        setMenuOpen(true);
      }
    } else {
      setMenuOpen(!menuOpen);
    }
  };

  const handleCloseMenuOnScroll = e => {
    return !e.target.className?.includes('c-input-select__menu-list');
  };

  return (
    <div
      className={classNames('c-input__group-select', extraClassNames, {
        'c-input--read-only c-input--disabled': readOnly || disabled,
        'c-input--outfaded': outfaded,
        'c-input--inline': inline,
        'c-input__group-select-standalone': standalone,
        'c-input-select--custom-multivalue': customMultiValue
      })}
      onClick={e => e.stopPropagation()}
    >
      {label && (
        <label
          htmlFor={id}
          className={classNames('c-input__label', {
            'u-hidden-visually': labelHiddenVisually
          })}
        >
          {label}
        </label>
      )}
      <div
        className={classNames('c-input__wrapper c-menu__wrapper', {
          'c-input__wrapper--select': !readOnly && !disabled
        })}
      >
        {labelGroup && ( //selectedItem &&
          <div className={classNames('c-input__sup-value')}>{labelGroup}</div>
        )}
        {readOnly ? (
          <input
            value={value?.label ?? ''}
            className={classNames('c-input')}
            readOnly
          />
        ) : (
          <Select
            id={id}
            ref={ref}
            name={name}
            options={options}
            value={value}
            isDisabled={disabled}
            isSearchable={isSearchable}
            noOptionsMessage={
              noOptionsMessage
                ? noOptionsMessage
                : val =>
                    val.inputValue
                      ? intl.formatMessage(
                          messages.searchInputGeneralFeedback,
                          {
                            count: 0,
                            total: 0,
                            searchTerm: val.inputValue,
                            b: chunks => <b>{chunks}</b>
                          }
                        )
                      : ''
            }
            isClearable={isClearable}
            isMulti={isMulti}
            onChange={onChange}
            onBlur={onBlur}
            className="c-input-select"
            classNamePrefix="c-input-select"
            styles={selectStyles}
            maxMenuHeight={maxMenuHeight}
            minMenuHeight={minMenuHeight}
            components={
              customMultiValue
                ? {
                    ClearIndicator,
                    Group,
                    DropdownIndicator,
                    MultiValueContainer,
                    ValueContainer
                  }
                : {
                    ClearIndicator,
                    Group,
                    DropdownIndicator,
                    SingleValue: CustomSingleValue
                  }
            }
            menuPlacement={'auto'}
            hideSelectedOptions={false}
            placeholder={placeholder ?? ''}
            menuIsOpen={menuOpen}
            onMenuOpen={handleMenuOpen}
            onMenuClose={() => setMenuOpen(false)}
            formatGroupLabel={formatGroupLabel}
            closeMenuOnScroll={handleCloseMenuOnScroll}
          />
        )}
      </div>
      {errors?.[name] &&
        touched?.[name] &&
        typeof errors[name] === 'string' && (
          <ErrorMessage name={name} component={InputErrorMessage} />
        )}

      {errors?.[name] &&
        touched?.[name] &&
        typeof errors[name] !== 'string' &&
        getIn(errors, name) && (
          <InputErrorMessage>{getIn(errors, name)}</InputErrorMessage>
        )}
    </div>
  );
};

export default FieldSelect;
